// The router previously ran in hash mode, so every URL in the wild — bookmarks,
// links in emails sent by the API, anything we can't retroactively rewrite — looks
// like https://admin.unrulr.com/#/secure/org/42/settings.
//
// The fragment is never sent to the server, so those requests arrive as a plain
// request for '/' and boot the app successfully. They just boot to the wrong place:
// the router sees path '/', redirects to '/login', and the intended destination is
// lost. Rewriting the URL before the router is constructed restores the old
// behavior without any server-side involvement.

const APP_BASE_PATH = '/';

/**
 * Returns the history-mode path a legacy hash URL should become, or null when the
 * URL isn't a legacy hash URL and should be left alone.
 */
export function resolveLegacyHashUrl(
  pathname: string,
  search: string,
  hash: string
): string | null {
  // Real anchor fragments (#section) never begin with a slash, which makes '#/'
  // the discriminator between a legacy route and an in-page anchor.
  if (!hash.startsWith('#/')) {
    return null;
  }

  // A non-root pathname already carries a real history-mode route; a '#/' fragment
  // alongside it isn't a legacy URL.
  if (pathname !== APP_BASE_PATH) {
    return null;
  }

  const target = hash.slice(1);

  // Anything the fragment carries itself — query, nested anchor — comes along as-is.
  if (target.includes('?')) {
    return target;
  }

  // Covers /?a=b#/secure/..., the shape produced when a third party appends query
  // params ahead of the fragment per normal URL semantics.
  if (search && search !== '?') {
    return `${target}${search}`;
  }

  return target;
}

/**
 * Rewrites a legacy hash URL in place. Must run before the router is constructed so
 * the initial navigation resolves against the corrected URL. Uses replaceState so
 * the stale URL doesn't become a history entry the back button bounces through.
 */
export function redirectLegacyHashUrl() {
  const { pathname, search, hash } = window.location;
  const target = resolveLegacyHashUrl(pathname, search, hash);

  if (target) {
    window.history.replaceState(null, '', target);
  }
}
